import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import ParliamentMemberCard from './ParliamentMemberCard';
import ParliamentMembersFilterSidebar from './ParliamentMembersFilterSidebar';
import { 
  extractUniqueHashtags, 
  applyParliamentMemberFilters,
  getParliamentMemberResultCounts
} from '../utils/filterUtils';

const MEMBERS_PER_PAGE = 9;

const ParliamentMembersPage = () => {
  // Data states
  const [allParliamentMembers, setAllParliamentMembers] = useState([]);
  const [loadedMembers, setLoadedMembers] = useState([]);
  const [allMeetings, setAllMeetings] = useState([]);
  const [allAgendaItems, setAllAgendaItems] = useState([]);
  const [allSpeeches, setAllSpeeches] = useState([]);
  const [memberSpeechData, setMemberSpeechData] = useState({});
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Refs to prevent duplicate loading
  const loadingRef = useRef(false);
  const initialLoadCompleted = useRef(false);

  // Filter states
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    selectedHashtags: [],
    selectedParties: [],
    searchText: ''
  });

  // Separate function for initial load to avoid conflicts with infinite scroll
  const loadInitialMembers = async (allMembers, agendaItems) => {
    try {
      // Clear any existing loaded members
      setLoadedMembers([]);
      setMemberSpeechData({});
      
      // Get first batch of members
      const membersBatch = allMembers.slice(0, MEMBERS_PER_PAGE);
      
      if (membersBatch.length === 0) {
        setHasMore(false);
        return;
      }

      const newSpeechData = {};

      // Process each member in the initial batch
      for (const member of membersBatch) {
        try {
          const firstName = member.first_name || '';
          const lastName = member.last_name || '';
          
          // Fetch speeches for this member using database pattern matching (accurate approach)
          const { data: memberSpeeches, error: memberSpeechesError } = await supabase
            .from('speeches')
            .select('*')
            .ilike('speaker_first_name', `%${firstName}%`)
            .ilike('speaker_last_name', `%${lastName}%`)
            .order('time', { ascending: false });

          if (memberSpeechesError) {
            console.error(`Error fetching speeches for ${firstName} ${lastName}:`, memberSpeechesError);
            newSpeechData[member.id] = {
              speechCount: 0,
              lastActive: null,
              topHashtags: []
            };
            continue;
          }

          const speeches = memberSpeeches || [];

          // Get unique agenda item IDs for this member's speeches
          const agendaItemIds = [...new Set(speeches.map(speech => speech.agenda_item_id))];
          
          // Get hashtags from agenda items where this member spoke
          const memberHashtags = [];
          
          agendaItemIds.forEach(agendaItemId => {
            const agendaItem = agendaItems.find(item => item.id === agendaItemId);
            if (agendaItem && agendaItem.hashtags && Array.isArray(agendaItem.hashtags)) {
              memberHashtags.push(...agendaItem.hashtags);
            }
          });

          // Count hashtag frequency and get top hashtags
          const hashtagCounts = {};
          memberHashtags.forEach(hashtag => {
            hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
          });

          const topHashtags = Object.entries(hashtagCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 4)
            .map(([hashtag]) => hashtag);

          newSpeechData[member.id] = {
            speechCount: speeches.length,
            lastActive: speeches.length > 0 ? speeches[0].time : null,
            topHashtags: topHashtags
          };
        } catch (error) {
          console.error(`Error processing member ${member.first_name} ${member.last_name}:`, error);
          newSpeechData[member.id] = {
            speechCount: 0,
            lastActive: null,
            topHashtags: []
          };
        }
      }

      // Set initial data
      setLoadedMembers(membersBatch);
      setMemberSpeechData(newSpeechData);
      setCurrentPage(0);
      
      // Check if there are more members to load
      if (membersBatch.length < MEMBERS_PER_PAGE || MEMBERS_PER_PAGE >= allMembers.length) {
        setHasMore(false);
      }

      initialLoadCompleted.current = true;

    } catch (error) {
      console.error('Error loading initial members:', error);
      setError('Failed to load initial members');
    }
  };

  // Fetch initial data (meetings, agenda items, speeches, and first batch of members)
  useEffect(() => {
    async function fetchInitialData() {
      try {
        setLoading(true);
        initialLoadCompleted.current = false;
        
        // Fetch all parliament members (for filtering purposes)
        const { data: allMembersData, error: allMembersError } = await supabase
          .from('parliament_members')
          .select('*')
          .order('last_name', { ascending: true });

        if (allMembersError) throw allMembersError;

        // Fetch all meetings for hashtags
        const { data: meetingsData, error: meetingsError } = await supabase
          .from('meetings')
          .select('*');

        if (meetingsError) throw meetingsError;

        // Fetch all agenda items for hashtags
        const { data: agendaItemsData, error: agendaItemsError } = await supabase
          .from('agenda_items')
          .select('*');

        if (agendaItemsError) throw agendaItemsError;

        // Fetch all speeches for activity data
        const { data: speechesData, error: speechesError } = await supabase
          .from('speeches')
          .select('*')
          .order('time', { ascending: false });

        if (speechesError) throw speechesError;

        setAllParliamentMembers(allMembersData || []);
        setAllMeetings(meetingsData || []);
        setAllAgendaItems(agendaItemsData || []);
        setAllSpeeches(speechesData || []);

        // Load initial batch of members using separate function
        await loadInitialMembers(allMembersData || [], agendaItemsData || []);

      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    }

    fetchInitialData();
  }, []);

  // Function to load additional batches of members (for infinite scroll only)
  const loadMoreMembersBatch = async (allMembers, agendaItems, page) => {
    try {
      const startIndex = page * MEMBERS_PER_PAGE;
      const endIndex = startIndex + MEMBERS_PER_PAGE;
      const membersBatch = allMembers.slice(startIndex, endIndex);

      if (membersBatch.length === 0) {
        setHasMore(false);
        return;
      }

      const newSpeechData = {};

      // Process each member in the batch using accurate database queries
      for (const member of membersBatch) {
        try {
          const firstName = member.first_name || '';
          const lastName = member.last_name || '';
          
          // Fetch speeches for this member using database pattern matching (accurate approach)
          const { data: memberSpeeches, error: memberSpeechesError } = await supabase
            .from('speeches')
            .select('*')
            .ilike('speaker_first_name', `%${firstName}%`)
            .ilike('speaker_last_name', `%${lastName}%`)
            .order('time', { ascending: false });

          if (memberSpeechesError) {
            console.error(`Error fetching speeches for ${firstName} ${lastName}:`, memberSpeechesError);
            newSpeechData[member.id] = {
              speechCount: 0,
              lastActive: null,
              topHashtags: []
            };
            continue;
          }

          const speeches = memberSpeeches || [];

          // Get unique agenda item IDs for this member's speeches
          const agendaItemIds = [...new Set(speeches.map(speech => speech.agenda_item_id))];
          
          // Get hashtags from agenda items where this member spoke
          const memberHashtags = [];
          
          agendaItemIds.forEach(agendaItemId => {
            const agendaItem = agendaItems.find(item => item.id === agendaItemId);
            if (agendaItem && agendaItem.hashtags && Array.isArray(agendaItem.hashtags)) {
              memberHashtags.push(...agendaItem.hashtags);
            }
          });

          // Count hashtag frequency and get top hashtags
          const hashtagCounts = {};
          memberHashtags.forEach(hashtag => {
            hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
          });

          const topHashtags = Object.entries(hashtagCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 4)
            .map(([hashtag]) => hashtag);

          newSpeechData[member.id] = {
            speechCount: speeches.length,
            lastActive: speeches.length > 0 ? speeches[0].time : null,
            topHashtags: topHashtags
          };
        } catch (error) {
          console.error(`Error processing member ${member.first_name} ${member.last_name}:`, error);
          newSpeechData[member.id] = {
            speechCount: 0,
            lastActive: null,
            topHashtags: []
          };
        }
      }

      // Append new data to existing data
      setLoadedMembers(prev => [...prev, ...membersBatch]);
      setMemberSpeechData(prev => ({ ...prev, ...newSpeechData }));
      
      // Check if there are more members to load
      if (endIndex >= allMembers.length) {
        setHasMore(false);
      }

    } catch (error) {
      console.error('Error loading members batch:', error);
      setError('Failed to load more members');
    }
  };

  // Load more members when scrolling
  const loadMoreMembers = useCallback(async () => {
    if (loadingRef.current || loadingMore || !hasMore || !initialLoadCompleted.current) return;

    loadingRef.current = true;
    setLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      await loadMoreMembersBatch(allParliamentMembers, allAgendaItems, nextPage);
      setCurrentPage(nextPage);
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [currentPage, hasMore, loadingMore, allParliamentMembers, allAgendaItems]);

  // Improved infinite scroll effect with throttling
  useEffect(() => {
    // Don't set up scroll listener until initial load is complete
    if (!initialLoadCompleted.current) return;

    let timeoutId = null;

    const handleScroll = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout to throttle scroll events
      timeoutId = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.offsetHeight;
        
        // Trigger load when user is within 200px of bottom
        const threshold = 200;
        const isNearBottom = scrollTop + windowHeight >= documentHeight - threshold;

        if (isNearBottom && !loadingRef.current && !loadingMore && hasMore) {
          loadMoreMembers();
        }
      }, 100); // Throttle to 100ms
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loadMoreMembers, loadingMore, hasMore, initialLoadCompleted.current]);

  // Apply filters to loaded members
  const filteredMembers = useMemo(() => {
    return applyParliamentMemberFilters(
      loadedMembers, 
      allSpeeches, 
      allAgendaItems, 
      filters,
      memberSpeechData
    );
  }, [loadedMembers, allSpeeches, allAgendaItems, filters, memberSpeechData]);

  // Get filter options
  const hashtagOptions = useMemo(() => {
    return extractUniqueHashtags(allMeetings, allAgendaItems);
  }, [allMeetings, allAgendaItems]);

  const partyOptions = useMemo(() => {
    const parties = [...new Set(allParliamentMembers.map(member => member.party).filter(Boolean))];
    return parties.sort().map(party => ({ value: party, label: party }));
  }, [allParliamentMembers]);


  // Get result counts
  const resultCounts = useMemo(() => {
    return getParliamentMemberResultCounts(filteredMembers);
  }, [filteredMembers]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      selectedHashtags: [],
      selectedParties: [],
      searchText: ''
    });
  };

  // Handle hashtag click - add to filter
  const handleHashtagClick = (hashtag) => {
    const newSelectedHashtags = filters.selectedHashtags.includes(hashtag)
      ? filters.selectedHashtags.filter(h => h !== hashtag) // Remove if already selected
      : [...filters.selectedHashtags, hashtag]; // Add if not selected
    
    setFilters({
      ...filters,
      selectedHashtags: newSelectedHashtags
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      <Header />

      <div className="flex flex-1 relative">
        {/* Mobile filter toggle */}
        <button
          onClick={() => setIsFilterOpen(true)}

          className="md:hidden fixed top-40 left-2 z-30 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm"

        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Filters
        </button>
        {/* Filter Sidebar */}
        <ParliamentMembersFilterSidebar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          hashtagOptions={hashtagOptions}
          partyOptions={partyOptions}
          onClearFilters={handleClearFilters}
          resultCounts={resultCounts}
          isMobileOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {isFilterOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsFilterOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Parliament Members
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Browse and filter parliament members by party and activity
            </p>
          </div>

          <ErrorMessage message={error} />

          {loading ? (
            <LoadingSpinner message="Loading parliament members..." />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMembers.map((member) => (
                  <ParliamentMemberCard 
                    key={member.id}
                    member={member}
                    speechData={memberSpeechData[member.id] || { speechCount: 0, lastActive: null, topHashtags: [] }}
                    onHashtagClick={handleHashtagClick}
                  />
                ))}
              </div>

              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex justify-center items-center py-8">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    <span className="text-gray-600 dark:text-gray-400">Loading more members...</span>
                  </div>
                </div>
              )}

              {/* End of data indicator */}
              {!hasMore && loadedMembers.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    All parliament members loaded ({loadedMembers.length} total)
                  </p>
                </div>
              )}

              {/* No results */}
              {!loading && filteredMembers.length === 0 && loadedMembers.length > 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">
                    No parliament members found
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    Try adjusting your filters to see more results
                  </p>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default ParliamentMembersPage;
